import React, { createContext, useContext, useState, useEffect } from 'react';
import { studentsApi, type Student } from '../api';
import { useAuth } from './AuthContext';

interface StudentContextType {
  currentStudent: Student | null;
  students: Student[];
  loading: boolean;
  setCurrentStudent: (student: Student) => void;
  refreshStudents: () => Promise<void>;
}

const StudentContext = createContext<StudentContextType>({
  currentStudent: null,
  students: [],
  loading: true,
  setCurrentStudent: () => {},
  refreshStudents: async () => {},
});

export function StudentProvider({ children }: { children: React.ReactNode }) {
  const [students, setStudents] = useState<Student[]>([]);
  const [currentStudent, setCurrentStudentState] = useState<Student | null>(null);
  const [loading, setLoading] = useState(true);
  const { isAuthenticated, user } = useAuth();

  const refreshStudents = async () => {
    if (!isAuthenticated) {
      setStudents([]);
      setCurrentStudentState(null);
      setLoading(false);
      return;
    }

    try {
      const res = await studentsApi.list();
      let list: Student[] = res.data.data;

      // 非管理员只能看到自己绑定的学生
      if (user?.role !== 'admin' && user?.studentId) {
        list = list.filter(s => s.id === user.studentId);
      }

      setStudents(list);

      // 恢复上次选择或默认
      const savedId = localStorage.getItem('currentStudentId');
      const saved = savedId ? list.find(s => s.id === parseInt(savedId)) : null;
      const defaultStudent = saved || list.find(s => s.is_default) || list[0];
      if (defaultStudent) setCurrentStudentState(defaultStudent);
    } catch (error) {
      console.error('Failed to load students:', error);
      setStudents([]);
      setCurrentStudentState(null);
    } finally {
      setLoading(false);
    }
  };

  const setCurrentStudent = (student: Student) => {
    setCurrentStudentState(student);
    localStorage.setItem('currentStudentId', String(student.id));
  };

  useEffect(() => {
    if (isAuthenticated) {
      setLoading(true);
      refreshStudents();
    } else {
      setStudents([]);
      setCurrentStudentState(null);
      setLoading(false);
    }
  }, [isAuthenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <StudentContext.Provider value={{ currentStudent, students, loading, setCurrentStudent, refreshStudents }}>
      {children}
    </StudentContext.Provider>
  );
}

export const useStudent = () => useContext(StudentContext);
